import { ReactNode, useState } from 'react'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import SensorsIcon from '@mui/icons-material/Sensors'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { JoyStickIcon } from '@/components/icons'
import useTranslation from '@/hooks/useTranslation'
import { usePermission } from '@/hooks/permissions'
import { useUnits } from '@/hooks/useUnits'
import { formatDecoderValue } from '@/constant/decoder'
import useStationLineUnitControl from '@/features/config/hooks/station/useStationLineUnitControl'
import useStationLineUnitDecoder from '@/features/config/hooks/station/useStationLineUnitDecoder'
import DeleteDecoderDialog from './DeleteDecoderDialog'
import { controllerTokens } from './uiTokens'

interface DecoderControlPanelProps {
  data: any
  // Grid tab drives the decoder type: '0' output, '1' input, '2' SDI-12.
  tab: string
  // Whether this decoder runs the mk3 line-unit protocol. Readings are only
  // shown for mk3 output decoders.
  isMk3?: boolean
  protocol?: number
  /**
   * SCRUM-1032 QC round 3, item 1 — whether this decoder's hardware can
   * autoswitch (see `canAutoswitch`). False hides the command rather than
   * disabling it: there is nothing the user can do to make it available.
   */
  canAutoswitch?: boolean
  // Page interface used when a row carries no own interfaceId (non-hub).
  fallbackInterfaceId?: number
  isDark?: boolean
  isCommandLoading?: boolean
  onOn: (id: any, address: any, rowKey: string, twiOriginalId: any) => void
  onOff: (id: any, address: any, rowKey: string, twiOriginalId: any) => void
  onPing: (id: any, address: any, rowKey: string, twiOriginalId: any) => void
  onAutoswitch: (
    id: any,
    address: any,
    rowKey: string,
    twiOriginalId: any,
  ) => void
  onClose?: () => void
  onDeleted?: () => void
}

type Reading = { label: string; value: ReactNode; wide?: boolean }

const PANEL_WIDTH = 348

// Physical magnitudes can't be negative, so the hardware's -1 "no reading"
// sentinel is shown as a dash.
const magnitude = (value: any, unit: string) =>
  value != null && Number(value) >= 0 ? `${value} ${unit}` : '—'

const DecoderControlPanel = ({
  data,
  tab,
  isMk3,
  protocol = 0,
  canAutoswitch = true,
  fallbackInterfaceId,
  isDark,
  isCommandLoading,
  onOn,
  onOff,
  onPing,
  onAutoswitch,
  onClose,
  onDeleted,
}: DecoderControlPanelProps) => {
  const { t } = useTranslation()
  const { isViewOnly } = usePermission()
  const { convertLabelUnit, convertFromMetric } = useUnits()
  const c = controllerTokens(isDark)

  const iface = Number(data?.interfaceId) || Number(fallbackInterfaceId) || 0
  const addr = Number(data?.address)
  const idx = Number(data?.index)
  const twiId = data?.twiId
  const rowKey = `${data?.address}-${data?.index}`
  const isOutput = tab === '0'
  // Line-unit / IO readings only apply to mk3 output decoders.
  const showReadings = isOutput && !!isMk3

  const {
    data: lineUnit,
    isFetching: loadingLU,
    refetch: refetchLU,
  } = useStationLineUnitControl(iface, addr, twiId)
  const {
    data: decoder,
    isFetching: loadingDec,
    refetch: refetchDec,
  } = useStationLineUnitDecoder(iface, addr, idx, twiId)


  const canOfferDelete = !!data?.id
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  // Readings are not fetched on open — the user pulls them with each section's
  // refresh button (they are live hardware reads, so they should be explicit).

  const ioOn = Number(data?.value) !== 0
  const ioNumber = Number(data?.value)

  const lineUnitReadings: Reading[] = [
    { label: t('lineResistance'), value: magnitude(lineUnit?.resistance, 'ohm') },
    {
      label: t('operationsTemperature'),
      value:
        lineUnit?.temperature != null
          ? `${convertFromMetric(lineUnit.temperature, 'C')} ${convertLabelUnit('C')}`
          : '—',
    },
    { label: t('commonUnitsVoltage'), value: magnitude(lineUnit?.voltage, 'mV') },
    { label: t('formsLabelLeak'), value: magnitude(lineUnit?.leak, 'mA') },
  ]

  const ioReadings: Reading[] = [
    {
      label: t('formsLabelValue'),
      value: formatDecoderValue(decoder?.rawValue, t),
    },
    {
      label: t('controllersReadingSwitchParameters'),
      wide: true,
      value: decoder?.switchParameters
        ? `${decoder.switchParameters.inrush} V · ${decoder.switchParameters.inrushTime} ms · ${decoder.switchParameters.hold} V`
        : '—',
    },
    {
      label: t('commonUnitsResistance'),
      value: decoder?.resistance != null ? `${decoder.resistance} ohm` : '—',
    },
    {
      label: t('commonUnitsInductance'),
      value: decoder?.inductance != null ? `${decoder.inductance} mH` : '—',
    },
  ]

  // ---- reusable bits --------------------------------------------------------
  const sectionLabelSx = {
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase' as const,
    color: c.muted,
  }

  const MetricTile = ({ label, value, wide }: Reading) => (
    <Box
      sx={{
        gridColumn: wide ? '1 / -1' : 'auto',
        backgroundColor: c.page,
        border: `1px solid ${c.line}`,
        borderRadius: '10px',
        p: '9px 12px',
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          ...sectionLabelSx,
          fontSize: '9.5px',
          color: c.faint,
          mb: '3px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: c.text,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  )

  const Section = ({
    title,
    loading,
    onRefresh,
    children,
  }: {
    title: string
    loading: boolean
    onRefresh: () => void
    children: ReactNode
  }) => (
    <Box sx={{ mt: '16px' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: '10px' }}
      >
        <Stack direction="row" alignItems="center" spacing="7px">
          <Box
            sx={{
              width: 3,
              height: 12,
              borderRadius: '2px',
              backgroundColor: c.accent,
            }}
          />
          <Typography sx={sectionLabelSx}>{title}</Typography>
        </Stack>
        <Box
          component="button"
          type="button"
          onClick={onRefresh}
          disabled={loading}
          sx={{
            width: 26,
            height: 26,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            border: `1px solid ${c.line}`,
            backgroundColor: 'transparent',
            color: c.accent,
            cursor: loading ? 'default' : 'pointer',
            transition: 'background-color .15s ease',
            '&:hover': { backgroundColor: c.hover },
          }}
        >
          {loading ? (
            <CircularProgress size={13} sx={{ color: c.accent }} />
          ) : (
            <RefreshIcon sx={{ fontSize: 15 }} />
          )}
        </Box>
      </Stack>
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ height: 74 }}>
          <CircularProgress size={18} sx={{ color: c.accent }} />
        </Stack>
      ) : (
        <Box
          sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}
        >
          {children}
        </Box>
      )}
    </Box>
  )

  const CommandButton = ({
    label,
    icon,
    fg,
    bg,
    border,
    onClick,
  }: {
    label: string
    icon: ReactNode
    fg: string
    bg: string
    border: string
    onClick: () => void
  }) => {
    const disabled = isViewOnly || isCommandLoading
    return (
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onClick()}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          p: '9px 4px',
          borderRadius: '11px',
          border: `1px solid ${border}`,
          backgroundColor: bg,
          color: fg,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          transition: 'transform .13s ease, filter .13s ease',
          '&:hover': {
            transform: disabled ? 'none' : 'translateY(-1px)',
            filter: disabled ? 'none' : 'brightness(1.06)',
          },
          '&:active': { transform: 'translateY(0)' },
        }}
      >
        {icon}
        <Typography
          component="span"
          sx={{ fontSize: '11px', fontWeight: 600, color: 'inherit' }}
        >
          {label}
        </Typography>
      </Box>
    )
  }

  const warnSoft = isDark ? 'rgba(238,205,29,0.14)' : 'rgba(181,84,10,0.10)'
  const vividSoft = isDark ? 'rgba(47,211,216,0.16)' : 'rgba(10,155,176,0.10)'

  return (
    <Box
      sx={{
        width: PANEL_WIDTH,
        overflow: 'hidden',
        borderRadius: '16px',
        backgroundColor: c.card,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: '15px 16px 14px',
          backgroundColor: c.head,
          borderBottom: `1px solid ${c.line}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing="11px">
          <Box
            sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: '11px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: c.chipFg,
              backgroundColor: c.chipBg,
              border: `1px solid ${isDark ? 'rgba(102,240,160,0.24)' : 'rgba(0,71,68,0.14)'}`,
            }}
          >
            <JoyStickIcon width={18} height={18} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '15px',
                color: c.textHi,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data?.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '11.5px',
                color: c.muted,
                mt: '2px',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {`${data?.address}-${data?.index}`}
              {data?.twiCustomName ? `  ·  ${data.twiCustomName}` : ''}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: '15px 16px 17px' }}>
        {/* IO value hero */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: '13px 15px',
            borderRadius: '12px',
            backgroundColor: c.page,
            border: `1px solid ${isOutput && ioOn ? `${c.accent}55` : c.line}`,
          }}
        >
          <Typography sx={sectionLabelSx}>{t('formsLabelValue')}</Typography>

          {isOutput ? (
            <Stack direction="row" alignItems="center" spacing="9px">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: ioOn ? c.accent : c.faint,
                  boxShadow: ioOn ? `0 0 9px ${c.accent}` : 'none',
                }}
              />
              <Typography
                sx={{
                  fontSize: '19px',
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: ioOn ? c.textHi : c.muted,
                }}
              >
                {ioOn ? t('mapViewStationOn') : t('mapViewStationOff')}
              </Typography>
            </Stack>
          ) : (
            <Typography
              sx={{
                fontSize: '23px',
                fontWeight: 800,
                lineHeight: 1,
                color: c.textHi,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {Number.isFinite(ioNumber) ? ioNumber : '—'}
            </Typography>
          )}
        </Box>

        {/* Device commands — output decoders only */}
        {isOutput && (
          <Stack direction="row" spacing="8px" sx={{ mt: '13px' }}>
            <CommandButton
              label={t('mapViewStationOn')}
              fg={c.onAccent}
              bg={c.accent}
              border={c.accent}
              icon={<PowerSettingsNewIcon sx={{ fontSize: 18 }} />}
              onClick={() =>
                onOn(data?.id, data?.address, rowKey, data?.twiOriginalId)
              }
            />
            <CommandButton
              label={t('mapViewStationOff')}
              fg={c.muted}
              bg={c.track}
              border={c.line}
              icon={<PowerOffIcon sx={{ fontSize: 18 }} />}
              onClick={() =>
                onOff(data?.id, data?.address, rowKey, data?.twiOriginalId)
              }
            />
            <CommandButton
              label={t('mapViewStationsPing')}
              fg={c.warn}
              bg={warnSoft}
              border={`${c.warn}55`}
              icon={<SensorsIcon sx={{ fontSize: 18 }} />}
              onClick={() =>
                onPing(data?.id, data?.address, rowKey, data?.twiOriginalId)
              }
            />
            {canAutoswitch && (
              <CommandButton
                label={t('Autoswitch')}
                fg={c.accentVivid}
                bg={vividSoft}
                border={`${c.accentVivid}55`}
                icon={<AutorenewIcon sx={{ fontSize: 18 }} />}
                onClick={() =>
                  onAutoswitch(
                    data?.id,
                    data?.address,
                    rowKey,
                    data?.twiOriginalId,
                  )
                }
              />
            )}
          </Stack>
        )}

        {/* Readings — mk3 output decoders only */}
        {showReadings && (
          <>
            <Section
              title={t('lineUnitReadings')}
              loading={loadingLU}
              onRefresh={() => refetchLU()}
            >
              {lineUnitReadings.map((r) => (
                <MetricTile key={r.label} {...r} />
              ))}
            </Section>

            <Section
              title={t('ioReadings')}
              loading={loadingDec}
              onRefresh={() => refetchDec()}
            >
              {ioReadings.map((r) => (
                <MetricTile key={r.label} {...r} />
              ))}
            </Section>
          </>
        )}

        {canOfferDelete && (
          <Box
            component="button"
            type="button"
            disabled={isViewOnly}
            onClick={() => !isViewOnly && setConfirmDeleteOpen(true)}
            sx={{
              mt: '16px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              p: '8px 12px',
              borderRadius: '11px',
              border: `1px solid ${c.danger}55`,
              backgroundColor: 'transparent',
              color: c.danger,
              cursor: isViewOnly ? 'not-allowed' : 'pointer',
              opacity: isViewOnly ? 0.45 : 1,
              transition: 'background-color .15s ease',
              '&:hover': {
                backgroundColor: isViewOnly ? 'transparent' : `${c.danger}14`,
              },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            <Typography
              component="span"
              sx={{ fontSize: '12px', fontWeight: 600, color: 'inherit' }}
            >
              {t('decoderDeleteTitle')}
            </Typography>
          </Box>
        )}
      </Box>

      {canOfferDelete && (
        <DeleteDecoderDialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          onDone={() => onClose?.()}
          onDeleted={onDeleted}
          interfaceId={iface}
          decoder={data}
          protocol={protocol}
          isDark={isDark}
        />
      )}
    </Box>
  )
}

export default DecoderControlPanel
